import { clsx } from 'clsx';

const PALETTE = [
  'bg-rose-100 text-rose-700',
  'bg-orange-100 text-orange-700',
  'bg-amber-100 text-amber-800',
  'bg-lime-100 text-lime-800',
  'bg-emerald-100 text-emerald-700',
  'bg-teal-100 text-teal-700',
  'bg-sky-100 text-sky-700',
  'bg-indigo-100 text-indigo-700',
  'bg-violet-100 text-violet-700',
  'bg-fuchsia-100 text-fuchsia-700',
] as const;

const SIZES = {
  xs: 'size-5 text-[9px]',
  sm: 'size-6 text-2xs',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
} as const;

export type AvatarSize = keyof typeof SIZES;

/** "Marie-Ève Tremblay" → "MT"; "pizza" → "PI"; "" → "?". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  if (!first) return '?';
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  const letters = last ? `${first[0]}${last[0]}` : first.slice(0, 2);
  return letters.toLocaleUpperCase();
}

/** Stable palette index for a name so the same person always gets the same color. */
function paletteIndex(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(hash) % PALETTE.length;
}

export type AvatarProps = {
  name: string;
  size?: AvatarSize;
  className?: string;
};

export function Avatar({ name, size = 'sm', className }: AvatarProps) {
  return (
    <span
      aria-hidden
      title={name}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold select-none',
        SIZES[size],
        PALETTE[paletteIndex(name)],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
